import type { OrganizationRepository } from "@/data/repositories/organizationRepository";
import type { OrganizationV2 } from "@/types/domain/organization";
import type { CreateOrganizationInput, UpdateOrganizationInput } from "@/data/repositories/organizationRepository";

/**
 * Seed data for the platform OrganizationV2 shape (richer than the
 * existing `src/types/organization.ts` the working OrganizationContext
 * reads — see that context for the one actually driving the UI today).
 */
const DUMMY_ORGANIZATIONS_V2: OrganizationV2[] = [
  {
    id: "org_01HZX0000000000000000001",
    name: "Rivera & Co. Weddings",
    slug: "rivera-co",
    organizationType: "independent_planner",
    timezone: "America/Mexico_City",
    currency: "MXN",
    countryCode: "MX",
    subscriptionPlan: "studio",
    subscriptionStatus: "trialing",
    createdBy: "usr_01HZX0000000000000000001",
    createdAt: "2024-01-15T09:00:00.000Z",
    updatedAt: "2024-01-15T09:00:00.000Z",
  },
  {
    id: "org_01HZX0000000000000000002",
    name: "Maison Blanc Events",
    slug: "maison-blanc",
    organizationType: "planning_agency",
    timezone: "America/Mexico_City",
    currency: "MXN",
    countryCode: "MX",
    subscriptionPlan: "agency",
    subscriptionStatus: "active",
    createdBy: "usr_01HZX0000000000000000002",
    createdAt: "2023-11-02T09:00:00.000Z",
    updatedAt: "2023-11-02T09:00:00.000Z",
  },
];

export class DummyOrganizationRepository implements OrganizationRepository {
  private static readonly STORAGE_KEY = "atelier.demo-organizations-v1";
  private organizations: OrganizationV2[];

  constructor() {
    this.organizations = this.load();
  }

  private load(): OrganizationV2[] {
    if (typeof window === "undefined") return DUMMY_ORGANIZATIONS_V2.map((organization) => ({ ...organization }));
    const stored = window.localStorage.getItem(DummyOrganizationRepository.STORAGE_KEY);
    if (stored === null) return DUMMY_ORGANIZATIONS_V2.map((organization) => ({ ...organization }));
    try {
      const parsed: unknown = JSON.parse(stored);
      return Array.isArray(parsed) ? (parsed as OrganizationV2[]) : DUMMY_ORGANIZATIONS_V2.map((organization) => ({ ...organization }));
    } catch {
      return DUMMY_ORGANIZATIONS_V2.map((organization) => ({ ...organization }));
    }
  }

  private persist() {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DummyOrganizationRepository.STORAGE_KEY, JSON.stringify(this.organizations));
    }
  }

  async listForUser(_userId: string): Promise<OrganizationV2[]> {
    // Dummy mode: every demo user sees every seeded organization, mirroring
    // the existing OrganizationContext behavior. A real implementation
    // joins through organization_members.
    return this.organizations;
  }

  async getById(id: string): Promise<OrganizationV2 | null> {
    return this.organizations.find((o) => o.id === id) ?? null;
  }

  async create(input: CreateOrganizationInput): Promise<OrganizationV2> {
    const now = new Date().toISOString();
    const organization: OrganizationV2 = {
      ...input,
      id: `org_${crypto.randomUUID()}`,
      subscriptionPlan: "starter",
      subscriptionStatus: "trialing",
      createdAt: now,
      updatedAt: now,
    };
    this.organizations.push(organization);
    this.persist();
    return organization;
  }

  async update(id: string, patch: UpdateOrganizationInput): Promise<OrganizationV2> {
    const index = this.organizations.findIndex((organization) => organization.id === id);
    const existing = this.organizations[index];
    if (!existing) throw new Error(`Organization ${id} not found`);
    const updated = { ...existing, ...patch, updatedAt: new Date().toISOString() };
    this.organizations[index] = updated;
    this.persist();
    return updated;
  }

  async delete(id: string): Promise<void> {
    if (!this.organizations.some((organization) => organization.id === id)) {
      throw new Error(`Organization ${id} not found`);
    }
    this.organizations = this.organizations.filter((organization) => organization.id !== id);
    this.persist();
  }
}
